// Minimal Markdown renderer for authored lesson explanations and tutor chat replies.
//
// Supports only the subset that content uses: ATX headings (#, ##, ###), **bold**,
// *italic*, `inline code`, GFM pipe tables, unordered lists, and paragraphs (single
// line breaks inside a paragraph are preserved — chat replies use them for structure).
// Content is either authored or produced by our own tutor, so this builds React
// elements directly (no dangerouslySetInnerHTML — nothing is interpreted as raw HTML).
// If it ever needs more (links, images, ordered lists), swap in react-markdown +
// remark-gfm.

import React from 'react';

// Split a single line into React nodes, honouring `code`, **bold**, and *italic*
// (code first so asterisks inside a code span aren't treated as emphasis; bold before
// italic so ** isn't read as an empty italic). Italic requires non-space characters
// just inside the asterisks so stray ones ("3 * 4 * 5") stay literal.
function renderInline(text, keyPrefix) {
  const nodes = [];
  const regex = /(`[^`]+`)|(\*\*[^*]+\*\*)|(\*\S(?:[^*]*\S)?\*)/g;
  let last = 0;
  let m;
  let i = 0;
  while ((m = regex.exec(text)) !== null) {
    if (m.index > last) nodes.push(text.slice(last, m.index));
    const tok = m[0];
    if (tok.startsWith('`')) {
      nodes.push(<code key={`${keyPrefix}-${i}`} className="bg-amber-100 text-amber-800 rounded px-1 py-0.5 text-[0.9em]">{tok.slice(1, -1)}</code>);
    } else if (tok.startsWith('**')) {
      nodes.push(<strong key={`${keyPrefix}-${i}`} className="font-semibold text-amber-900">{tok.slice(2, -2)}</strong>);
    } else {
      nodes.push(<em key={`${keyPrefix}-${i}`} className="italic">{tok.slice(1, -1)}</em>);
    }
    last = m.index + tok.length;
    i++;
  }
  if (last < text.length) nodes.push(text.slice(last));
  return nodes;
}

function splitRow(line) {
  let s = line.trim();
  if (s.startsWith('|')) s = s.slice(1);
  if (s.endsWith('|')) s = s.slice(0, -1);
  return s.split('|').map(c => c.trim());
}

const isSeparator = (line) => /^[\s|:-]+$/.test(line) && line.includes('-');

function parseBlocks(md) {
  const lines = String(md).replace(/\r\n/g, '\n').split('\n');
  const blocks = [];
  let i = 0;

  while (i < lines.length) {
    const line = lines[i];
    if (!line.trim()) { i++; continue; }

    const h = line.match(/^(#{1,6})\s+(.*)$/);
    if (h) { blocks.push({ type: 'h', level: h[1].length, text: h[2] }); i++; continue; }

    // GFM table: a row with pipes immediately followed by a dashed separator row.
    if (line.includes('|') && i + 1 < lines.length && isSeparator(lines[i + 1])) {
      const header = splitRow(line);
      i += 2;
      const rows = [];
      while (i < lines.length && lines[i].includes('|') && lines[i].trim()) {
        rows.push(splitRow(lines[i]));
        i++;
      }
      blocks.push({ type: 'table', header, rows });
      continue;
    }

    if (/^\s*[-*]\s+/.test(line)) {
      const items = [];
      while (i < lines.length && /^\s*[-*]\s+/.test(lines[i])) {
        items.push(lines[i].replace(/^\s*[-*]\s+/, ''));
        i++;
      }
      blocks.push({ type: 'ul', items });
      continue;
    }

    // Paragraph: gather lines until a blank line or the start of another block.
    // Single line breaks are kept (rendered via whitespace-pre-wrap) — tutor replies
    // use them for structure, e.g. short numbered lists.
    const para = [line];
    i++;
    while (i < lines.length && lines[i].trim() &&
      !/^(#{1,6})\s+/.test(lines[i]) && !/^\s*[-*]\s+/.test(lines[i]) && !lines[i].includes('|')) {
      para.push(lines[i]);
      i++;
    }
    blocks.push({ type: 'p', text: para.join('\n') });
  }
  return blocks;
}

const HEADING_CLASS = {
  1: 'text-2xl font-bold text-amber-800 mt-6 mb-3 first:mt-0',
  2: 'text-xl font-bold text-amber-800 mt-5 mb-2 first:mt-0',
  3: 'text-lg font-semibold text-amber-700 mt-4 mb-2 first:mt-0',
};

export default function Markdown({ children, className = '' }) {
  const blocks = parseBlocks(children || '');
  return (
    <div className={className}>
      {blocks.map((b, idx) => {
        if (b.type === 'h') {
          const Tag = `h${Math.min(b.level, 3)}`;
          return <Tag key={idx} className={HEADING_CLASS[Math.min(b.level, 3)]}>{renderInline(b.text, `h${idx}`)}</Tag>;
        }
        if (b.type === 'ul') {
          return (
            <ul key={idx} className="list-disc pl-6 my-3 first:mt-0 last:mb-0 space-y-1 text-amber-800">
              {b.items.map((it, j) => <li key={j}>{renderInline(it, `li${idx}-${j}`)}</li>)}
            </ul>
          );
        }
        if (b.type === 'table') {
          return (
            <div key={idx} className="my-4 first:mt-0 last:mb-0 overflow-x-auto rounded-lg border border-amber-100">
              <table className="w-full text-sm">
                <thead className="bg-amber-50">
                  <tr>
                    {b.header.map((cell, j) => (
                      <th key={j} className="px-3 py-2 text-left font-semibold text-amber-800 border-b border-amber-100">
                        {renderInline(cell, `th${idx}-${j}`)}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {b.rows.map((row, r) => (
                    <tr key={r} className={r % 2 ? 'bg-amber-50/40' : ''}>
                      {row.map((cell, j) => (
                        <td key={j} className="px-3 py-2 text-amber-900 border-b border-amber-50">
                          {renderInline(cell, `td${idx}-${r}-${j}`)}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          );
        }
        return <p key={idx} className="my-3 first:mt-0 last:mb-0 text-amber-800 leading-relaxed whitespace-pre-wrap">{renderInline(b.text, `p${idx}`)}</p>;
      })}
    </div>
  );
}
